import { EVENT_INTERACTION } from '@eventbrite/consumer-search.event-renderer';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { EventsInteractionsTracker } from './eventInteractionsTracker';
import { InteractionsEngagementLogger } from './loggers/InteractionsEngagementLogger';
import { InteractionsDatadogLogger } from './loggers/InteractionsDatadogLogger';
import { InteractionsStatsigLogger } from './loggers/InteractionsStatsigLogger';
import {
  EventInteraction,
  EventInteractionWithContext,
  InteractionCustomEvent,
  PLACE_EVENT_INTERACTION,
  TrackableElement,
} from './types';

let eventInteractionTracker: EventsInteractionsTracker;
let datadogLogger: InteractionsDatadogLogger;

/**
 * Initializes the global event tracking system
 *
 * This function:
 * - Creates a single EventsInteractionsTracker instance for the page
 * - Sets up event listeners and batching
 */
function startEventTracking() {
  const IS_SSR = typeof document === 'undefined';

  if (IS_SSR || eventInteractionTracker) {
    return;
  }

  datadogLogger = new InteractionsDatadogLogger();
  eventInteractionTracker = new EventsInteractionsTracker(document, [
    new InteractionsEngagementLogger(),
    datadogLogger,
    new InteractionsStatsigLogger(),
  ]);

  eventInteractionTracker.listen();
  eventInteractionTracker.track();
}

/**
 * React hook for tracking event interactions
 *
 * This hook:
 * - Initializes the global tracking system (singleton)
 * - Listens for EVENT_INTERACTION events
 * - Transforms them into PLACE_EVENT_INTERACTION events with context
 * - Returns a ref to attach to the DOM element containing event interactions
 *
 * @param getTrackingInformation - Function that adds context to interactions
 * @returns Object with ref to attach to the container element
 *
 * @example
 * ```tsx
 * const { ref } = useTracking(() => ({
 *   place: { name: 'related-events', position: 0 },
 *   user: { locale: 'en' }
 * }));
 *
 * return <div ref={ref}>...</div>;
 * ```
 */
export function useTracking<T extends TrackableElement>(
  getTrackingInformation: (eventInteraction: EventInteraction) => EventInteractionWithContext,
) {
  useMemo(() => startEventTracking(), []);
  const logInteractions = useCallback(
    (e: InteractionCustomEvent) => {
      e.preventDefault();

      const detail: EventInteractionWithContext = {
        ...e.detail,
        ...getTrackingInformation(e.detail),
      };

      // Dispatch the enhanced event for EventsInteractionsTracker to pick up
      const placeEventInteractionEvent = new CustomEvent(PLACE_EVENT_INTERACTION, { detail, bubbles: true });
      e.currentTarget?.dispatchEvent(placeEventInteractionEvent);

      // Datadog integration for promoted events
      if (detail.event?.isPromoted && datadogLogger) {
        datadogLogger.trackEventCardInteraction(detail);
      }
    },
    [getTrackingInformation],
  );

  const ref = useRef<T>(null);

  useEffect(() => {
    const container = ref.current;
    container?.addEventListener(EVENT_INTERACTION, logInteractions);
    return () => container?.removeEventListener(EVENT_INTERACTION, logInteractions);
  }, [ref, logInteractions]);

  return { ref };
}
